// Shared Toolkit V3 JSON parsing helpers.
// Toolkit content can contain "broken JSON" (unescaped quotes inside string values) coming from legacy data,
// so we need a lenient parser for both resources and assessments.

export function parseJsonLenient(raw: any): any | null {
	try {
		if (raw && typeof raw === 'object') return raw;

		let s = (raw || '').toString().trim();
		if (!s) return null;

		// Common HTML-escaped quotes (seen in older payloads).
		s = s.replace(/&quot;/g, '"').replace(/&#34;/g, '"').replace(/&#39;/g, "'");

		let parsed: any = null;
		try {
			parsed = JSON.parse(s);
		} catch (e) {
			// Try extracting a JSON object substring then repairing it.
			const a = s.indexOf('{');
			const b = s.lastIndexOf('}');
			if (a >= 0 && b > a) {
				const candidate = s.slice(a, b + 1);
				try {
					parsed = JSON.parse(candidate);
				} catch (e2) {
					parsed = JSON.parse(repairLikelyBrokenJson(candidate));
				}
			} else {
				parsed = JSON.parse(repairLikelyBrokenJson(s));
			}
		}

		// Handle double-encoded JSON
		if (typeof parsed === 'string') {
			parsed = JSON.parse(parsed);
		}
		return parsed;
	} catch (e) {
		return null;
	}
}

// Repairs invalid JSON produced by unescaped quotes inside string values.
// Strategy: when inside a JSON string and we see an unescaped `"` that is NOT followed by a valid string terminator,
// we escape it (turn it into `\"`) and continue.
export function repairLikelyBrokenJson(input: string): string {
	let out = '';
	let inString = false;
	let escaped = false;

	const isTerminator = (ch: string) => ch === ',' || ch === '}' || ch === ']' || ch === ':' || ch === '\n' || ch === '\r';

	const nextNonWs = (s: string, i: number) => {
		for (let k = i; k < s.length; k++) {
			const c = s[k];
			if (c !== ' ' && c !== '\t') return c;
		}
		return '';
	};

	const looksLikeNextObjectKey = (s: string, commaPos: number) => {
		// After a real string terminator in an object, we often see: , "key" :
		let i = commaPos + 1;
		// skip ws
		while (i < s.length && (s[i] === ' ' || s[i] === '\t' || s[i] === '\n' || s[i] === '\r')) i++;
		if (s[i] !== '"') return false;
		i++; // into key
		// scan to closing quote (key shouldn't contain unescaped quotes)
		while (i < s.length) {
			if (s[i] === '\\') {
				i += 2;
				continue;
			}
			if (s[i] === '"') break;
			i++;
		}
		if (i >= s.length || s[i] !== '"') return false;
		i++;
		while (i < s.length && (s[i] === ' ' || s[i] === '\t')) i++;
		return s[i] === ':';
	};

	for (let i = 0; i < input.length; i++) {
		const ch = input[i];
		if (escaped) {
			out += ch;
			escaped = false;
			continue;
		}
		if (ch === '\\') {
			out += ch;
			escaped = true;
			continue;
		}

		if (ch === '"') {
			if (!inString) {
				inString = true;
				out += ch;
				continue;
			}

			// We are in a string; decide if this quote closes the string.
			const nxt = nextNonWs(input, i + 1);
			let closes = false;
			if (isTerminator(nxt)) {
				if (nxt === ',') {
					// Only treat as a true string terminator if the comma is followed by a JSON object key pattern: ,"key":
					const commaPos = input.indexOf(',', i + 1);
					closes = commaPos >= 0 && looksLikeNextObjectKey(input, commaPos);
				} else {
					closes = true;
				}
			}

			if (closes) {
				inString = false;
				out += ch;
			} else {
				// Internal unescaped quote -> escape it.
				out += '\\"';
			}
			continue;
		}

		out += ch;
	}

	return out;
}

